import { FEES, FIRM } from "@/lib/firm";

/**
 * Visible, in-depth local content for the two priority conveyancing pages
 * (/conveyancing-sunshine-coast and /conveyancing/caloundra). Kept visible
 * (not collapsed) so search engines and AI assistants weight it fully.
 */
export type LocalDepth = {
  area: string;
  shortAnswer: string;
  costRows: { item: string; amount: string; note: string }[];
  costNote: string;
  transactionTypes: { t: string; d: string }[];
  localSections: { h: string; p: string[] }[];
  conveyancerVsSolicitor: string[];
  extraFaqs: { q: string; a: string }[];
};

const COMMON_COSTS = [
  { item: "Our professional fee — sale", amount: FEES.sale, note: "Fixed, incl. GST" },
  { item: "Our professional fee — purchase", amount: FEES.purchase, note: "Fixed, incl. GST" },
  {
    item: "Searches and government fees (buyers)",
    amount: "about $450–$800",
    note: "At cost — varies with the property type and council",
  },
  {
    item: "Transfer duty (buyers)",
    amount: "Depends on price",
    note: "Paid to the Queensland Revenue Office; first home concessions may apply",
  },
];

const VS = [
  "In Queensland, conveyancing can be done by a solicitor or under a solicitor's supervision. The difference shows up when something goes wrong: a finance or building and pest dispute, a caveat on the title, a deceased seller, or a special condition that needs renegotiating.",
  "A solicitor can give you legal advice on those problems and act for you in a dispute. Your file stays with the same person instead of being referred out halfway through the contract.",
  `At ${FIRM.name}, your file is run by a solicitor and the fee is still fixed. You get legal advice at a set conveyancing price.`,
];

export const SUNSHINE_COAST_DEPTH: LocalDepth = {
  area: "the Sunshine Coast",
  shortAnswer: `Fixed-fee conveyancing on the Sunshine Coast with ${FIRM.name}: ${FEES.sale} to sell and ${FEES.purchase} to buy, both including GST, handled by a solicitor from our Golden Beach office in Caloundra. We review your contract free before you sign. If the contract ends under cooling-off, finance or building and pest, we charge no professional fee.`,
  costRows: COMMON_COSTS,
  costNote:
    "Your quote lists each search separately before we start. The fee is the same whether the property is in Caloundra, Maroochydore, Noosa or the hinterland.",
  transactionTypes: [
    {
      t: "Buying a house",
      d: "We review the contract and Form 2 disclosure before you sign. Then we run the searches, track your finance and building and pest dates, and settle through PEXA.",
    },
    {
      t: "Selling a property",
      d: "We prepare the seller disclosure statement (Form 2), check the contract the agent drafts, handle the buyer's requests and organise discharge of your mortgage.",
    },
    {
      t: "Units and apartments",
      d: "We get and actually read the body corporate records: levies, sinking fund forecast, insurance, by-laws, defects, disputes and any letting or caretaking agreement.",
    },
    {
      t: "Off the plan",
      d: "We go through the disclosure statement, sunset date, variation rights and your rights if the finished product differs from the plans.",
    },
  ],
  localSections: [
    {
      h: "Units on the coastal strip: Kings Beach to Mooloolaba",
      p: [
        "Much of the coast's housing is in body corporate schemes. In older beachfront buildings, the real risk is usually in the records rather than the contract: a sinking fund that is too small for the building's age, a special levy on the way for concrete or balcony repairs, or rising insurance costs.",
        "If you plan to holiday-let, the by-laws, the community management statement and any letting agreement decide whether you can. We check them against your plans before cooling-off ends.",
      ],
    },
    {
      h: "Canal and waterfront homes: Pelican Waters, Golden Beach and Kawana",
      p: [
        "Waterfront lots often come with revetment walls, pontoons and jetties. Repairs are usually the owner's responsibility, and the structures may need approvals. We ask the right questions and tell you what the searches and the seller's disclosure show.",
      ],
    },
    {
      h: "New estates and off the plan: Aura, Baringa and Maroochydore",
      p: [
        "New estates and apartment projects come with developer disclosure, building covenants, sunset dates and settlement timing that depends on registration. Pay particular attention to the clauses that let the developer change the product or extend dates.",
      ],
    },
    {
      h: "Hinterland and acreage: Buderim, Palmwoods, Maleny and beyond",
      p: [
        "Rural and semi-rural property needs a wider set of searches: water and bore access, on-site sewerage, easements and access, vegetation mapping, and whether sheds and dwellings have been approved. We scope these at the start so nothing surprises you late.",
      ],
    },
    {
      h: "Sunshine Coast Council overlays",
      p: [
        "Flood, coastal hazard, erosion-prone, landslide and bushfire overlays can affect insurance and what you can build later. We point out the overlays that apply and explain what they mean for how you plan to use the property.",
      ],
    },
  ],
  conveyancerVsSolicitor: VS,
  extraFaqs: [
    {
      q: "How long does conveyancing take on the Sunshine Coast?",
      a: "Most contracts settle 30 to 45 days after signing, but the contract sets the date. A cash purchase can be quicker. An off-the-plan purchase settles when the plan is registered, which can take months or years.",
    },
    {
      q: "Do I need to come into the office?",
      a: "No. Identity checks, signing and settlement can all be done electronically. You are welcome to come into our Golden Beach office, but most Sunshine Coast clients never need to.",
    },
  ],
};

export const CALOUNDRA_DEPTH: LocalDepth = {
  area: "Caloundra",
  shortAnswer: `Fixed-fee conveyancing in Caloundra with ${FIRM.name}: ${FEES.sale} to sell and ${FEES.purchase} to buy, both including GST. Our office is at ${FIRM.street}, Golden Beach, five minutes from the Caloundra town centre, and a solicitor runs your file. We review your contract free before you sign.`,
  costRows: COMMON_COSTS,
  costNote:
    "Unit purchases need a body corporate search, and canal lots may need more enquiries. Both are listed in your quote at cost before we start.",
  transactionTypes: [
    {
      t: "Kings Beach and Bulcock Beach units",
      d: "We go through the body corporate records properly, including the sinking fund, upcoming levies, insurance and short-stay by-laws, before cooling-off ends.",
    },
    {
      t: "Older homes at Moffat, Shelly and Dicky Beach",
      d: "These homes may have extensions, pools or decks that were never approved. Coastal wear can also turn a minor building report item into a major cost. We help you work out what to raise before the building and pest deadline.",
    },
    {
      t: "Pelican Waters and Golden Beach canal homes",
      d: "We look at revetment walls, pontoons and the approvals for any works over the water, and explain who pays when they need repair.",
    },
    {
      t: "Downsizers and estate sales",
      d: "Many Caloundra sales involve downsizing, a retirement village move or a deceased estate. We can handle the probate, power of attorney or capacity questions as well as the sale.",
    },
  ],
  localSections: [
    {
      h: "Buying a unit in Caloundra",
      p: [
        "Caloundra's beachfront buildings range from 1970s walk-ups to new high-rise towers. With older buildings, the age of the structure and the health of the sinking fund matter more than the asking price. A building that needs balcony or concrete repairs can bring a special levy shortly after you settle.",
        "We order the body corporate search, read the minutes of recent meetings, and tell you in plain English what is planned and who pays for it.",
      ],
    },
    {
      h: "Selling in Caloundra",
      p: [
        "Since 1 August 2025, most Queensland sellers must give buyers a disclosure statement (Form 2) before the contract is signed. If it is wrong or incomplete, the buyer may be able to end the contract. We prepare it from current searches so your sale holds together.",
      ],
    },
    {
      h: "Caloundra's suburbs and estates",
      p: [
        "We act across Caloundra and the surrounding suburbs: Kings Beach, Moffat Beach, Shelly Beach, Dicky Beach, Currimundi, Little Mountain, Meridan Plains, Battery Hill, Pelican Waters, Golden Beach, and the new estates at Baringa and Aura. Each area raises different issues, and we deal with them every week.",
      ],
    },
  ],
  conveyancerVsSolicitor: VS,
  extraFaqs: [
    {
      q: "Where is your Caloundra office?",
      a: `At ${FIRM.street}, Golden Beach, Caloundra, on the Pumicestone Passage foreshore about five minutes from the Caloundra town centre. Parking is easy, but most clients never need to come in.`,
    },
    {
      q: "Do you charge extra for a unit?",
      a: "No. The professional fee is the same. The body corporate search is a disbursement charged at cost and listed in your quote.",
    },
  ],
};
